import React from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import dayjs from 'dayjs'
import { toMoney } from '@mobi/utils/money'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { getHostHelpers } from '@mobi/betslip/helpers/hostHelpers'
import {
  BetSlipItem,
  BettingType,
  Campaign,
  CampaignV2,
  MultiInvestment,
} from '@mobi/betslip/types'
import { useAppSelector } from '@mobi/betslip/Store/hooks'
import { setActiveInvestment } from '@mobi/betslip/Store/Workflow'
import { setSelectedCampaignForItem, setSelectedCampaignForMulti } from '@mobi/betslip/Store/Bets'
import { PromoSelector } from '@mobi/betslip/Components/Common/PromoSelector'
import { selectBonusCashCampaigns, selectCampaigns } from '@mobi/betslip/Store/Workflow/selectors'
import { useBonusAppliedItems } from '@mobi/betslip/hooks/useBonusAppliedItems'
import { selectBetSlipItemIsEachWay } from '@mobi/betslip/Store/Bets/selectors'
import { BetSlipContext } from '@mobi/betslip/context'

interface BonusBetAndCashProps {
  id: string
  bettingType: BettingType | 'MULTI-BETTING-TYPE'
  isMulti?: boolean
  selectedCampaign?: Campaign
  multiInvestment?: MultiInvestment
}

export const BonusBetAndCash = ({
  id,
  bettingType,
  isMulti = false,
  selectedCampaign,
  multiInvestment,
}: BonusBetAndCashProps) => {
  const [bonusBetToSplit, setBonusBetToSplit] = React.useState<CampaignV2 | null>(null)

  const dispatch = useDispatch()

  const items = useAppSelector(state => state.betslip.bets.items)

  const isEachWay = useAppSelector(selectBetSlipItemIsEachWay(id))

  const { betSlipItems: bonusUpdatedItems, remainingBonusCashCampaigns } =
    useBonusAppliedItems(items)

  const availableBonusCashCampaign = (remainingBonusCashCampaigns.find(
    campaign => campaign.remainingAmount > 0
  ) ?? []) as Campaign

  const campaigns = useSelector(selectCampaigns)

  const bonusCashCampaigns = useSelector(selectBonusCashCampaigns)

  const bonusCashCampaignsForMulti = bonusCashCampaigns.filter(
    campaign => campaign.id === availableBonusCashCampaign?.id
  )

  const bonusCashRewardsCampaigns =
    id === 'Multi'
      ? bonusCashCampaignsForMulti
      : getSelectedBonusCashCampaigns(
          campaigns,
          selectedCampaign,
          bonusUpdatedItems,
          availableBonusCashCampaign as Campaign,
          id
        )

  const bonusBetRewardsCampaigns = campaigns?.filter(
    (campaign: Campaign) => campaign.rewardType === 'BonusBetReward'
  )

  const useFeature = getHostHelpers().useFeature
  const groupBonusBets = getHostHelpers().groupBonusBets
  const isCampaignV2 = getHostHelpers().isCampaignV2
  const BonusBetSplittingTray = getHostHelpers().BonusBetSplittingTray
  const useSplitBonusBetMutation = getHostHelpers().useSplitBonusBetMutation

  const isSplitBonusFeatureActive = useFeature(FeatureFlags.SPLIT_BONUS_BETS.key)
  const isCampaignsV2FeatureActive = useFeature(FeatureFlags.GET_CAMPAIGNS_V2.key)

  const isBetSplittingAndCampaignsV2Active = isSplitBonusFeatureActive && isCampaignsV2FeatureActive

  const groupedBonusBets =
    isBetSplittingAndCampaignsV2Active && bonusBetRewardsCampaigns
      ? groupBonusBets(bonusBetRewardsCampaigns)
      : {}

  const bonusBetRewardsData = isBetSplittingAndCampaignsV2Active
    ? Object.keys(groupedBonusBets).map(key => groupedBonusBets[key][0])
    : bonusBetRewardsCampaigns

  const isSplitBonusBetTrayOpen = !!bonusBetToSplit

  const toastEmitter = React.useContext(BetSlipContext)?.toastEmitter

  const { mutate, isPending } = useSplitBonusBetMutation({
    updateBonusBetToSplit: () => setBonusBetToSplit(null),
    toastEmitter,
  })

  const filteredCampaigns = isEachWay
    ? [...bonusCashRewardsCampaigns]
    : [...bonusBetRewardsData, ...bonusCashRewardsCampaigns]

  const workflowStatus = useAppSelector(state => state.betslip.workflow.currentStatus)

  if (workflowStatus !== 'ready') {
    return null
  }

  const selections: Selections = []

  filteredCampaigns?.forEach(campaign => {
    const campaignDescription = campaign.description
      ? campaign.description.length > 20
        ? campaign.description.slice(0, 20) + '...'
        : campaign.description
      : ''

    if (canShowCampaign(campaign.rewardType, bettingType as BettingType)) {
      selections.push({
        isSelected: isMulti
          ? multiInvestment?.bonusBetId === campaign.id
          : selectedCampaign?.id === campaign.id,
        title:
          campaign.rewardType === 'BonusCashReward'
            ? `${toMoney(campaign.remainingAmount)} Bonus Cash`
            : `${toMoney(campaign.remainingAmount)} Bonus Bet`,
        passedCampaign: campaign,
        onClick: (bonusBetInvestmentType: 'Win' | 'Place') => {
          dispatch(setActiveInvestment(null))
          if (isMulti) {
            dispatch(setSelectedCampaignForMulti({ campaign }))
          } else {
            dispatch(
              setSelectedCampaignForItem({
                id,
                campaign,
                bonusBetInvestmentType,
                bonusCashCampaigns,
              })
            )
          }
        },
        descriptionSlots: [
          <div key='first'>{campaignDescription}</div>,
          <div key='second'> Expiry: {dayjs(campaign.rewardExpiry).format('DD/MM/YY')}</div>,
        ],
        ...(isCampaignV2(campaign) &&
          isBetSplittingAndCampaignsV2Active && {
            betsRemaining: groupedBonusBets[campaign.id.toString()]?.length,
            openSplitBonusBetTray: (campaign: CampaignV2) => setBonusBetToSplit(campaign),
          }),
      })
    }
  })

  return (
    <>
      <PromoSelector
        key={`promo-selector-${id}-${isEachWay}`}
        promoType='Bonus'
        itemId={id}
        selections={selections}
      />
      {isSplitBonusFeatureActive
        ? createPortal(
            <BonusBetSplittingTray
              bonusBetToSplit={bonusBetToSplit}
              isTrayOpen={isSplitBonusBetTrayOpen}
              closeTray={() => setBonusBetToSplit(null)}
              confirmSplitBonusBet={mutate}
              isBonusBetSplitPending={isPending}
            />,
            document.body
          )
        : null}
    </>
  )
}

// ======
// Helper
// ======

const canShowCampaign = (rewardType: string, bettingType: BettingType) => {
  if (rewardType !== 'BonusCashReward' && bettingType === 'tote-racing') {
    return false
  }
  return true
}

const getSelectedBonusCashCampaigns = (
  campaigns: Campaign[] | undefined,
  selectedCampaign: Campaign | undefined,
  bonusUpdatedItems: BetSlipItem[],
  availableBonusCashCampaign: Campaign | undefined,
  id: string
): Campaign[] => {
  const selectedCampaigns: Campaign[] = []

  if (!campaigns) return selectedCampaigns

  const selectedExists = campaigns.some(
    campaign => campaign.id === selectedCampaign?.id && campaign.rewardType === 'BonusCashReward'
  )

  if (selectedExists) {
    const matchedCampaign = campaigns.find(campaign => campaign.id === selectedCampaign?.id)
    if (matchedCampaign) {
      selectedCampaigns.push(matchedCampaign)
    }
  } else {
    for (const campaign of campaigns.filter(
      campaign => campaign.rewardType === 'BonusCashReward'
    )) {
      if (!selectedCampaign?.id) {
        const currentItemIndex = bonusUpdatedItems.findIndex(item => item.id === id)

        const nextAssignedCampaign = bonusUpdatedItems
          .slice(currentItemIndex + 1)
          .find(item => item.campaign?.id && item.campaign?.rewardType === 'BonusCashReward')

        if (nextAssignedCampaign?.campaign?.id === campaign.id) {
          selectedCampaigns.push(campaign)
          break
        }

        if (availableBonusCashCampaign?.id && campaign.id === availableBonusCashCampaign.id) {
          selectedCampaigns.push(campaign)
          break
        }
      } else {
        const finalCampaign = campaigns.find(
          campaign => campaign.id === availableBonusCashCampaign?.id
        )
        if (finalCampaign) selectedCampaigns.push(finalCampaign)
        break
      }
    }
  }

  return selectedCampaigns
}

// ======
// Types
// ======

type Selections = React.ComponentProps<typeof PromoSelector>['selections']
